/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";

import Stack from "@mui/material/Stack";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import {
  markModuleRead,
  fetchUnreadCount,
} from "app/redux/slice/notificationSlice";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import { useNavigate } from "react-router-dom";
import { istDateDisplay } from "app/helpers/istDate";
import {
  OFFICE_PURCHASE_ORDERS_ADD,
  officeInvoiceAddFromPO,
  officePurchaseOrderEdit,
} from "app/routes/constant";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  deletePurchaseOrder,
  getPurchaseOrderList,
} from "app/redux/slice/purchaseOrderSlice";
import {
  getSupplierList,
  getVendorRatingColor,
} from "app/redux/slice/supplierSlice";
import Span from "@jumbo/shared/Span";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";

// Approval lives on the Pre-Book Order screen, not here: App/Website orders
// land as pre-orders (approved there) and a pre-order can only be converted
// once approved, so by the time a PO exists it is always approved.
const PurchaseOrder = () => {
  const navigate = useNavigate();

  const { stageAction } = useDisclosure();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const purchaseOrderData = useSelector((state) => state?.purchaseOrder);
  const supplierData = useSelector((state) => state?.supplier);

  const [searchText, setSearchText] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [supplierId, setSupplierId] = useState();
  const [isInvoiceCreated, setIsInvoiceCreated] = useState("Pending");

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const {
    isOpen: isGenerateInvoice,
    onClose: closeGenerateInvoice,
    actionProps: generateInvoiceActionProps,
    stageAction: generateInvoiceStageAction,
  } = useDisclosure();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "purchaseOrder" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
    generateInvoicePermission: false,
  });

  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
        generateInvoicePermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
        generateInvoicePermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "create-purchase-order") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-purchase-order") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-purchase-order") {
            permissionValue.updatePermission = true;
          }
          if (num?.name === "generate-invoice") {
            permissionValue.generateInvoicePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  const columns = [
    { id: "poNumber", label: "PO No.", sort: true, minWidth: 90 },
    {
      id: "supplier",
      label: "Belongs To",
      sort: false,
      render: (row) => row?.supplier?.user?.name,
    },
    {
      id: "vendor",
      label: "Vendor",
      sort: true,
      // Coloured by the vendor's 1-5 rating; unrated vendors stay plain.
      render: (row) => (
        <Span
          style={{
            color: getVendorRatingColor(row?.supplier?.rating),
            fontWeight: row?.supplier?.rating ? 600 : 400,
          }}
        >
          {row?.supplier?.name}
        </Span>
      ),
    },
    { id: "deliveryType", label: "Del.Status", sort: true },
    { id: "totalQuantity", label: "Qty", sort: true },
    {
      id: "poDate",
      label: "Date",
      sort: true,
      minWidth: 100,
      render: (row) => istDateDisplay(row?.poDate),
    },
    {
      id: "godownPercentage",
      label: "Godown Progress",
      align: "center",
      render: (row) => {
        const pct = Math.min(row?.godownPercentage ?? 0, 100);
        const color =
          pct >= 100 ? "#16a34a" : pct >= 50 ? "#ae520b" : "#dc2626";

        return (
          <div style={{ width: "100%", minWidth: 120 }}>
            {/* Label */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
                fontSize: 12,
                fontWeight: 500,
                color,
              }}
            >
              <span>Godown</span>
              <span>{pct}%</span>
            </div>

            {/* Track */}
            <div
              style={{
                width: "100%",
                height: 8,
                backgroundColor: "#e5e7eb",
                borderRadius: 99,
                overflow: "hidden",
              }}
            >
              {/* Fill */}
              <div
                style={{
                  height: "100%",
                  width: `${pct}%`,
                  backgroundColor: color,
                  borderRadius: 99,
                  transition: "width 0.4s ease",
                }}
              />
            </div>

            {/* Completed text */}
            <div
              style={{
                textAlign: "right",
                fontSize: 11,
                color: "#6b7280",
                marginTop: 3,
              }}
            >
              {pct >= 100 ? "✓ Completed" : "In Progress"}
            </div>
          </div>
        );
      },
    },
    {
      id: "isInvoiceCreated",
      label: "Gen Status",
      align: "center",
      render: (row) =>
        row?.isInvoiceCreated === false ? (
          <Span style={{ color: "red" }}>Pending</Span>
        ) : (
          <Span style={{ color: "green" }}>Generated</Span>
        ),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 200,
      align: "center",
      render: (row) => (
        <>
          <IconButton
            disabled={!permission.updatePermission}
            onClick={() => {
              stageAction(row);
              navigate(officePurchaseOrderEdit(row._id));
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            disabled={
              !permission.generateInvoicePermission ||
              row?.isInvoiceCreated !== false
            }
            onClick={() => {
              generateInvoiceStageAction(row);
            }}
          >
            <ReceiptIcon />
          </IconButton>
          <IconButton
            disabled={!permission.deletePermission}
            onClick={() => {
              deleteStageAction(row);
            }}
          >
            <DeleteIcon color={"error"} />
          </IconButton>
        </>
      ),
    },
  ];

  const DeletePurchaseOrder = () => {
    closeDelete();
    dispatch(deletePurchaseOrder(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };
  const GenerateInvoice = () => {
    closeGenerateInvoice();
    navigate(officeInvoiceAddFromPO(generateInvoiceActionProps?._id));
  };

  const getData = () => {
    localStorage.setItem("purchase-order-search", searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(supplierId && { supplierId: supplierId._id }),
      ...(isInvoiceCreated !== "All" && {
        isInvoiceCreated: isInvoiceCreated === "Pending" ? false : true,
      }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getPurchaseOrderList(value));
    // Mark notifications as read whenever list is fetched
    dispatch(markModuleRead("Purchase Order (PO)")).then(() => {
      dispatch(fetchUnreadCount("Purchase Order (PO)"));
    });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  useEffect(() => {
    getPermission();
    dispatch(getSupplierList({ sort: 1, field: "name", type: "Office" }));
  }, []);

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  return (
    <>
      {purchaseOrderData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <JumboAutoComplete
                      options={supplierData?.list || []}
                      label="Vendor"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`supplierId`}
                      value={supplierId}
                      onChange={(evt, value) => setSupplierId(value)}
                      // renderOption and sx pass through JumboAutoComplete's
                      // {...rest}; renderInput cannot be overridden there.
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          key={option?._id}
                          style={{ color: getVendorRatingColor(option?.rating) }}
                        >
                          {option?.name}
                        </li>
                      )}
                      sx={{
                        "& .MuiAutocomplete-input": {
                          color: getVendorRatingColor(supplierId?.rating),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Invoice Generate Status</InputLabel>
                      <Select
                        name="isInvoiceCreated"
                        id="isInvoiceCreated"
                        value={isInvoiceCreated}
                        label="isInvoiceCreated"
                        size="small"
                        onChange={(e) => setIsInvoiceCreated(e.target.value)}
                      >
                        <MenuItem></MenuItem>
                        {["All", "Generated", "Pending"].map((val) => {
                          return <MenuItem value={val}>{val}</MenuItem>;
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Purchase Order List
                </Typography>
                {permission.createPermission && (
                  <LoadingButton
                    onClick={() => {
                      navigate(OFFICE_PURCHASE_ORDERS_ADD);
                    }}
                    sx={{
                      mt: { xs: 1, md: 0 },
                    }}
                    type="submit"
                    variant="contained"
                  >
                    Add Purchase Order
                  </LoadingButton>
                )}
              </Stack>
              <Table
                columns={columns}
                rows={purchaseOrderData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={purchaseOrderData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>
          <DialogConfirm
            isOpen={isGenerateInvoice}
            onClose={closeGenerateInvoice}
            title="Are you sure you want to generate invoice?"
            onConfirm={GenerateInvoice}
            btnLabels={{ cancel: "Cancel", confirm: "Generate" }}
          />
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete the purchase order?"
            onConfirm={DeletePurchaseOrder}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default PurchaseOrder;
